import React, {useEffect, useState} from "react";
import './style.scss';
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import axios from "axios";
import {isAuthenticated} from "../../Utils/Authentication";
import CircularProgress from "@material-ui/core/CircularProgress";
import LogoImage from "../../../resources/images/ShipitLogo.png";
import Direction from "../../Utils/Direction/Direction";
import {EnhancedTableHead} from "../../Components/Utils/TableUtils/EnhancedTableHead";
import {getComparator, stableSort} from "../../Components/Utils/TableUtils/Functions";

const headCells = [
    {id: 'name', label: 'Name'},
    {id: 'acceptedCount', label: 'Accepted'},
    {id: 'readyCount', label: 'Ready'},
    {id: 'onTheWayCount', label: 'Running'},
    {id: 'deliveredCount', label: 'Delivered'}
];

export default function AllStatus(props) {
    const [tableRows, setTableRows] = useState(undefined);
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('deliveredCount');

    async function getUid() {
        const authentication = await isAuthenticated();

        if (authentication.isAuthenticated) {
            return authentication.uid
        } else {
            props.history.push('/login');

            return undefined;
        }
    }

    async function getBids(uid) {
        const {data: bids} = await axios.get('/api/v2a/getUserBusinesses', {
            params: {uid}
        });

        return bids;
    }

    async function getBusinesses(bids) {
        const businessesFetchingPromises = bids.map(bid => () => axios.get('/api/v2a/getBusiness', {params: {bid}}));
        const businessesFetchingConcurrency = 50;

        const businessesIndex = {};

        while (businessesFetchingPromises.length) {
            // getBusinessesConcurrency at at time
            const results = await Promise.all(businessesFetchingPromises.splice(0, businessesFetchingConcurrency).map(f => f()))

            for (let result of results) {
                const {data: business} = result;

                const bid = business.id;

                businessesIndex[bid] = business;
            }
        }

        return businessesIndex
    }

    async function getOrders(bids) {
        const ordersFetchingPromises = bids.map(bid => () => axios.get('/api/v2a/getOrders', {params: {bid}}));
        const ordersFetchingConcurrency = 3;

        const businessesOrdersIndex = {};

        while (ordersFetchingPromises.length) {
            // getBusinessesConcurrency at at time
            const results = await Promise.all(ordersFetchingPromises.splice(0, ordersFetchingConcurrency).map(f => f()))

            for (let result of results) {
                const {data: orders} = result;

                if (orders.length > 0) {
                    const bid = orders[0].bid;

                    businessesOrdersIndex[bid] = orders;
                }
            }
        }

        return businessesOrdersIndex
    }

    function secondsSince(date) {
        return (new Date() - date) / 1000
    }

    function summarizeBusinessOrders(orders) {
        let acceptedCount = {total: 0, pastHour: 0};
        let readyCount = {total: 0, pastHour: 0};
        let onTheWayCount = {total: 0, pastHour: 0};
        let deliveredCount = {total: 0, pastHour: 0};

        for (let currOrder of orders) {
            switch (currOrder.status) {
                case "ACCEPTED":
                    acceptedCount.total++;

                    if (secondsSince(new Date(currOrder.orderTime)) / 60 / 60 < 1)
                        acceptedCount.pastHour++;
                    break;
                case "READY":
                    readyCount.total++

                    if (secondsSince(new Date(currOrder.readyTime)) / 60 / 60 < 1)
                        readyCount.pastHour++;
                    break;
                case "ASSIGNED":
                case "COLLECTED":
                    onTheWayCount.total++;

                    const statusChangeTime = currOrder.status === "ASSIGNED" ? currOrder.assignedTime : currOrder.collectedTime;

                    if (secondsSince(new Date(statusChangeTime)) / 60 / 60 < 1)
                        onTheWayCount.pastHour++;
                    break;
                case "DELIVERED":
                    deliveredCount.total++;

                    if (secondsSince(new Date(currOrder.deliveredTime)) / 60 / 60 < 1)
                        deliveredCount.pastHour++;
                    break;
            }
        }

        return {acceptedCount, readyCount, onTheWayCount, deliveredCount}
    }

    function summarizeBusinessesOrders(bids, ordersIndex) {
        const businessesOrdersSummaryIndex = {};

        for (const bid of bids) {
            const orders = bid in ordersIndex ? ordersIndex[bid] : [];
            businessesOrdersSummaryIndex[bid] = summarizeBusinessOrders(orders);
        }

        return businessesOrdersSummaryIndex;
    }

    async function initData() {
        const uid = await getUid();
        const bids = await getBids(uid);
        const businessesIndex = await getBusinesses(bids);
        const ordersIndex = await getOrders(bids);
        const businessesOrdersSummaryIndex = summarizeBusinessesOrders(bids, ordersIndex);

        const tmpRows = [];

        for (let business of Object.values(businessesIndex)) {
            const ordersSummary = businessesOrdersSummaryIndex[business.id];

            tmpRows.push({
                id: business.id,
                name: business.name,
                acceptedCount: ordersSummary.acceptedCount,
                readyCount: ordersSummary.readyCount,
                onTheWayCount: ordersSummary.onTheWayCount,
                deliveredCount: ordersSummary.deliveredCount
            });
        }

        setTableRows(tmpRows);
    }

    useEffect(() => {
        initData();
    }, []);

    if (!tableRows) {
        return (
            <div className="loading-container">
                <CircularProgress size={80} thickness={2}/>
            </div>
        )
    }

    function onRequestSort(event, property) {
        setOrder(orderBy === property && order === 'desc' ? 'asc' : 'desc');
        setOrderBy(property);
    }

    return (
        <Direction direction="rtl">
            <div className="allstatus-page">
                <div className="allstatus-page-content">
                    <div className="page-title">
                        <div className="logo">
                            <img src={LogoImage}/>
                        </div>
                        <div className="title-text">
                            Businesses Status
                        </div>
                    </div>
                    <div className="allstatus-table-container">
                        <TableContainer className="allstatus-table" component={Paper}>
                            <Table stickyHeader aria-label="simple table">
                                <EnhancedTableHead
                                    headCells={headCells}
                                    order={order}
                                    orderBy={orderBy}
                                    onRequestSort={onRequestSort}
                                    includeDeleteCell={false}
                                />
                                <TableBody>
                                    {
                                        stableSort(tableRows,
                                            getComparator({
                                                    "asc": "desc",
                                                    "desc": "asc"
                                                }[order],
                                                orderBy,
                                                (count) => count.total)).map(row => (
                                            <TableRow className="all-status-table-row" key={row.id}>
                                                <TableCell>{row.name}</TableCell>
                                                <TableCell>
                                                    <span
                                                        className="past-hour-text">({row.acceptedCount.pastHour})</span>
                                                    &nbsp;
                                                    {row.acceptedCount.total}
                                                </TableCell>
                                                <TableCell>
                                                    <span className="past-hour-text">({row.readyCount.pastHour})</span>
                                                    &nbsp;
                                                    {row.readyCount.total}
                                                </TableCell>
                                                <TableCell>
                                                    <span
                                                        className="past-hour-text">({row.onTheWayCount.pastHour})</span>
                                                    &nbsp;
                                                    {row.onTheWayCount.total}
                                                </TableCell>
                                                <TableCell>
                                                    <span
                                                        className="past-hour-text">({row.deliveredCount.pastHour})</span>
                                                    &nbsp;
                                                    {row.deliveredCount.total}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                </div>
            </div>
        </Direction>
    )
}